import {
  IStorageAdapter,
  StorageFile,
  UploadOptions,
  DownloadOptions,
} from './storage-adapter.interface';

/**
 * NAS Storage 어댑터
 * 프록시 서버를 통해 NAS(WebDAV)와 통신
 * 
 * 프록시 서버 엔드포인트:
 * - POST /api/upload - 파일 업로드
 * - GET /api/download/:path - 파일 다운로드 URL
 * - DELETE /api/delete/:path - 파일 삭제
 * - GET /api/exists/:path - 파일 존재 여부
 * - GET /api/metadata/:path - 파일 메타데이터
 */
export class NASStorageAdapter implements IStorageAdapter {
  private proxyUrl: string;

  constructor(proxyUrl?: string) {
    // 환경변수 또는 매개변수에서 프록시 서버 URL 가져오기
    this.proxyUrl = proxyUrl || 
      process.env.REACT_APP_NAS_PROXY_URL || 
      'http://localhost:3001';
  }

  /**
   * 프록시 서버 URL 설정
   */
  setProxyUrl(url: string): void {
    this.proxyUrl = url;
  }

  /**
   * 파일 업로드
   * FormData를 사용하여 프록시 서버로 전송
   */
  async upload(
    file: File,
    path: string,
    options?: UploadOptions
  ): Promise<StorageFile> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('path', path);

    // 메타데이터 추가
    if (options?.contentType) {
      formData.append('contentType', options.contentType);
    }
    if (options?.metadata) {
      formData.append('metadata', JSON.stringify(options.metadata));
    }

    try {
      const response = await fetch(`${this.proxyUrl}/api/upload`, {
        method: 'POST',
        body: formData,
        // 진행률 추적은 XMLHttpRequest를 통해 구현 가능 (추후 확장)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'NAS 업로드 실패');
      }

      const result = await response.json();

      return {
        path: result.path,
        url: result.url,
        size: result.size,
        contentType: result.contentType || file.type,
        createdAt: new Date(result.createdAt || Date.now()),
        metadata: result.metadata,
      };
    } catch (error) {
      console.error('NAS 업로드 에러:', error);
      throw error;
    }
  }

  /**
   * 파일 다운로드 URL 가져오기
   * 프록시 서버를 통한 다운로드 URL 반환
   */
  async getDownloadURL(
    path: string,
    options?: DownloadOptions
  ): Promise<string> {
    try {
      // 썸네일 경로 변환
      let targetPath = path;
      if (options?.quality === 'thumbnail') {
        targetPath = path.replace('/images/', '/thumbnails/');
        targetPath = targetPath.replace(/(\.\w+)$/, '_thumb$1');
      }

      // 프록시 서버 다운로드 엔드포인트
      const encodedPath = encodeURIComponent(targetPath);
      const url = `${this.proxyUrl}/api/download?path=${encodedPath}`;

      // 📌 HEAD 요청 제거 (Cold Start 최적화)
      // URL 생성 후 바로 반환 - 브라우저가 직접 다운로드 시도
      return url;
    } catch (error) {
      console.error('NAS 다운로드 URL 생성 에러:', error);
      throw error;
    }
  }

  /**
   * 파일 삭제
   */
  async delete(path: string): Promise<void> {
    try {
      const encodedPath = encodeURIComponent(path);
      const response = await fetch(
        `${this.proxyUrl}/api/file?path=${encodedPath}`,
        { method: 'DELETE' }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'NAS 파일 삭제 실패');
      }
    } catch (error) {
      console.error('NAS 파일 삭제 에러:', error);
      throw error;
    }
  }

  /**
   * 파일 존재 여부 확인
   */
  async exists(path: string): Promise<boolean> {
    try {
      const encodedPath = encodeURIComponent(path);
      const response = await fetch(
        `${this.proxyUrl}/api/file/exists?path=${encodedPath}`
      );

      if (!response.ok) {
        return false;
      }

      const result = await response.json();
      return result.exists === true;
    } catch (error) {
      console.error('NAS 파일 존재 확인 에러:', error);
      return false;
    }
  }

  /**
   * 파일 메타데이터 조회
   */
  async getMetadata(path: string): Promise<StorageFile> {
    try {
      const encodedPath = encodeURIComponent(path);
      const response = await fetch(
        `${this.proxyUrl}/api/file/metadata?path=${encodedPath}`
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'NAS 메타데이터 조회 실패');
      }

      const result = await response.json();

      return {
        path: result.path,
        url: result.url || await this.getDownloadURL(path),
        size: result.size,
        contentType: result.contentType || 'application/octet-stream',
        createdAt: new Date(result.createdAt || Date.now()),
        metadata: result.metadata,
      };
    } catch (error) {
      console.error('NAS 메타데이터 조회 에러:', error);
      throw error;
    }
  }

  /**
   * NAS 프록시 서버 연결 테스트
   */
  async testConnection(): Promise<{ success: boolean; message: string }> {
    try {
      const response = await fetch(`${this.proxyUrl}/api/health`);
      
      if (!response.ok) {
        return {
          success: false,
          message: `프록시 서버 응답 오류: ${response.status}`,
        };
      }

      const result = await response.json();
      return {
        success: true,
        message: result.message || '연결 성공',
      };
    } catch (error: any) {
      return {
        success: false,
        message: `연결 실패: ${error.message}`,
      };
    }
  }

  /**
   * 폴더 생성
   * NAS에 폴더를 생성합니다.
   */
  async createDirectory(path: string): Promise<void> {
    try {
      const response = await fetch(`${this.proxyUrl}/api/directory`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ path }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'NAS 폴더 생성 실패');
      }

      console.log('✅ NAS 폴더 생성 완료:', path);
    } catch (error) {
      console.error('NAS 폴더 생성 에러:', error);
      throw error;
    }
  }

  /**
   * 여러 폴더 일괄 생성
   */
  async createDirectories(paths: string[]): Promise<void> {
    for (const path of paths) {
      try {
        await this.createDirectory(path);
      } catch (error) {
        console.error('폴더 생성 실패:', path, error);
        // 폴더가 이미 존재하는 경우는 무시
      }
    }
  }

  /**
   * 여러 파일 삭제
   * NAS에서는 URL이 아닌 경로를 직접 사용
   */
  async deleteMultiple(paths: string[]): Promise<void> {
    const deletePromises = paths.map(async (path) => {
      try {
        await this.delete(path);
      } catch (error) {
        console.error('파일 삭제 실패:', path, error);
      }
    });

    await Promise.all(deletePromises);
  }

  /**
   * 일괄 업로드 (여러 파일)
   */
  async uploadMultiple(
    files: Array<{ file: File; path: string; options?: UploadOptions }>
  ): Promise<StorageFile[]> {
    const uploadPromises = files.map(({ file, path, options }) =>
      this.upload(file, path, options)
    );

    return await Promise.all(uploadPromises);
  }
}

// 싱글톤 인스턴스 생성
export const nasStorageAdapter = new NASStorageAdapter();
